import React, { useEffect, useRef, ReactElement } from 'react';
import { TweenLite } from 'gsap';

import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import actions from 'src/actions';
import { connect } from 'react-redux';

const DialogWizardContext = React.createContext(undefined);

export const useDialogWizardContext = () => {
    const context = React.useContext(DialogWizardContext);

    if (!context) {
        throw new Error('This component must be used within a <DialogWizard /> component.');
    }

    return context;
};

interface IDialogWizardProgressBar {
    currentStep: number;
}

const DialogWizardProgressBar: React.FC<IDialogWizardProgressBar> = () => {
    const context = useDialogWizardContext();

    const pages = context.children.filter(
        (c: { type: { displayName: string }; props: { excludeFromProgress: boolean } }) =>
            c.type.displayName === 'DialogWizardPage' && !c.props.excludeFromProgress
    );
    const percentWidth = Math.floor(100 / pages.length);

    const percentStyle = {
        width: `${percentWidth}%`,
    };

    return (
        <div className="dlc-dialog-wizard-progress-indicator">
            {pages.map((p: React.ReactElement<IDialogWizardPageProps>, index: number) => {
                const stateClassName = context.currentStep === index ? 'current' : index < context.currentStep ? 'complete' : '';

                return (
                    <div className={`dlc-dialog-wizard-progress-indicator-step ${stateClassName}`} style={percentStyle} key={`step-${index}`}>
                        {p.props.title}
                        <span className="visual" />
                    </div>
                );
            })}
        </div>
    );
};

DialogWizardProgressBar.displayName = 'DialogWizardProgressBar';

interface IDialogWizardPageProps {
    title: string;
    excludeFromProgress?: boolean;
    onCloseDialog?: () => void;
    onNavigateToStep?: (step: number) => void;
}

export const DialogWizardPage: React.FC<IDialogWizardPageProps> = (props) => {
    useDialogWizardContext();

    return (
        <>
            <hr style={{ marginBottom: '1rem' }} />
            {React.cloneElement(props.children as ReactElement, {
                ...(props.children as ReactElement).props,
                onCancel: props.onCloseDialog,
            })}
        </>
    );
};

DialogWizardPage.defaultProps = {
    excludeFromProgress: false,
};

DialogWizardPage.displayName = 'DialogWizardPage';

interface IDialogWizardOwnProps {
    title: string;
    dialogName: string;
    showProgress?: boolean;
    currentStep: number;
    onChangeStep: (step: number) => void;
}

interface IDialogWizardStateProps {
    isOpen: boolean;
}

interface IDialogWizardDispatchProps {
    hideDialog: () => void;
}

type IDialogWizardProps = IDialogWizardOwnProps & IDialogWizardDispatchProps & IDialogWizardStateProps;

const DialogWizard: React.FC<IDialogWizardProps & { children: ReactElement[] }> = (props) => {
    const backgroundRef = useRef(null);
    const dialogRef = useRef(null);

    useEffect(() => {
        if (props.isOpen) {
            TweenLite.to(backgroundRef.current, 0.3, { opacity: '1' });
            TweenLite.to(dialogRef.current, 0.2, { delay: 0.2, translateY: '-75%', opacity: '1' });
        }
    });

    const onCloseDialog = () => {
        TweenLite.to(dialogRef.current, 0.2, { translateY: '-75%', opacity: '0' });
        TweenLite.to(backgroundRef.current, 0.3, { delay: 0.2, opacity: '0' });
        setTimeout(() => {
            props.onChangeStep(0);
            props.hideDialog();
        }, 500);
    };

    const onNavigateToStep = (step: number) => {
        props.onChangeStep(step);
    };

    const calcCurrentStep = () => {
        return props.currentStep === 4 ? 1 : props.currentStep;
    };

    return (
        <>
            {props.isOpen && (
                <div ref={backgroundRef} className="dlc-dialog-background no-display">
                    <div ref={dialogRef} className="dlc-dialog">
                        <div className="dlc-dialog-close" onClick={onCloseDialog} />
                        <div className="dlc-dialog-header">{props.title}</div>
                        <div className="dlc-dialog-body">
                            <DialogWizardContext.Provider value={props}>
                                <DialogWizardProgressBar currentStep={calcCurrentStep()} />
                                {React.cloneElement(props.children[props.currentStep] as ReactElement, {
                                    ...(props.children[props.currentStep] as ReactElement).props,
                                    onCloseDialog,
                                    onNavigateToStep,
                                })}
                            </DialogWizardContext.Provider>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const mapStateToProps = (state: IRootState, ownProps: IDialogWizardOwnProps) => ({
    isOpen: selectors.layout.isDialogShowing(state, ownProps.dialogName),
});

const mapDispatchToProps: IDialogWizardDispatchProps = {
    hideDialog: () => actions.layout.hideDialog(),
};

export default connect(mapStateToProps, mapDispatchToProps)(DialogWizard);



// WEBPACK FOOTER //
// ./src/components/common/DialogWizard.redesign.tsx